import {
  setConfig,
  getConfig,
  isInitialized,
  logger,
  eventBus,
  sessionManager,
  initHttpClient,
} from './core';
import { eventsService } from './api/events';
import { productsService } from './api/products';
import { contactService } from './api/contact';
import { getCartState } from './modules/cart/cartState';
import type {
  SDKConfig,
  SDKEventPayload,
  SDKEventHandler,
  Event,
  Product,
  Customer,
  EventInfo,
  ListEventsOptions,
  AvailabilityResponse,
  CheckoutResponse,
  UpdateOrderResponse,
  UpdateOrderLineItem,
  ContactFormRequest,
  ContactFormResponse,
  PublicOrder,
  UTMParams,
} from './types';

// Re-export types
export * from './types';

/**
 * Puulso SDK
 * 
 * Headless SDK for ticket purchasing, cart management, and digital ticket rendering.
 * 
 * @example
 * ```javascript
 * // Initialize the SDK
 * Puulso.init({
 *   clientKey: 'your-api-key',
 *   debug: true,
 * });
 * 
 * // Use headless API
 * const events = await Puulso.events.list();
 * ```
 */
export const Puulso = {
  /**
   * Initialize the SDK with configuration
   */
  init(config: SDKConfig): void {
    if (isInitialized()) {
      logger.warn('SDK already initialized. Reinitializing with new config.');
    }

    // Validate required config
    if (!config.clientKey) {
      throw new Error('clientKey is required');
    }

    // Set config
    const fullConfig = setConfig(config);

    // Initialize logger
    logger.setDebug(fullConfig.debug || false);
    logger.info('SDK initializing...', { version: '1.0.0' });

    // Initialize HTTP client
    initHttpClient({
      baseUrl: fullConfig.apiBaseUrl!,
      clientKey: fullConfig.clientKey,
    });

    // Initialize session
    sessionManager.getSessionId();

    // Emit initialized event
    eventBus.emit('tm:initialized', { config: fullConfig });
    logger.info('SDK initialized successfully');
  },

  /**
   * Check if SDK is initialized
   */
  isInitialized(): boolean {
    return isInitialized();
  },

  /**
   * Get current configuration
   */
  getConfig(): SDKConfig {
    return getConfig();
  },

  /**
   * Events API - fetch event data
   */
  events: {
    /**
     * List live events with optional filtering
     */
    async list(options?: ListEventsOptions): Promise<Event[]> {
      return eventsService.list(options);
    },

    /**
     * Get a single event by ID
     */
    async get(eventId: string): Promise<Event> {
      return eventsService.get(eventId);
    },

    /**
     * Get availability for an event on a specific date
     */
    async getAvailability(eventId: string, date: string, time?: string): Promise<AvailabilityResponse> {
      return eventsService.getAvailability(eventId, date, time);
    },
  },

  /**
   * Products API - fetch merchandise data
   */
  products: {
    /**
     * List all available products
     */
    async list(): Promise<Product[]> {
      return productsService.list();
    },

    /**
     * Get a single product by ID
     */
    async get(productId: string): Promise<Product> {
      return productsService.get(productId);
    },
  },

  /**
   * Cart API - manage shopping cart
   */
  cart: {
    /**
     * Add a ticket to the cart
     */
    async addTicket(
      eventId: string,
      eventInfo: Omit<EventInfo, 'zoneName' | 'zoneNameEs' | 'menuName' | 'menuNameEs'>,
      quantity: number,
      utm?: UTMParams
    ): Promise<string | null> {
      return getCartState().addTicket(eventId, eventInfo, quantity, utm);
    },

    /**
     * Add a product to the cart
     */
    async addProduct(productId: string, quantity: number): Promise<string | null> {
      return getCartState().addProduct(productId, quantity);
    },

    /**
     * Update orderLines for an existing order
     * Only allowed for orders with status "Created" or "Pending"
     */
    async update(orderId: string, orderLines: UpdateOrderLineItem[]): Promise<UpdateOrderResponse> {
      return getCartState().update(orderId, orderLines);
    },

    /**
     * Validate a discount code for a specific event
     */
    async validateCode(eventId: string, date: string, timeSlot: string, code: string): Promise<{ valid: boolean; percentage: number; remainingUses: number }> {
      return getCartState().validateCode(eventId, date, timeSlot, code);
    },

    async checkout(orderId: string, customer: Customer, language?: string, discountCode?: string): Promise<CheckoutResponse> {
      return getCartState().checkout(orderId, customer, language, discountCode);
    },

    /**
     * Get order details
     */
    async get(orderId: string, authToken: string): Promise<PublicOrder> {
      return getCartState().get(orderId, authToken);
    }
  },

  /**
   * Contact API - submit contact form
   */
  contact: {
    /**
     * Submit a contact form
     */
    async submit(contact: ContactFormRequest): Promise<ContactFormResponse> {
      return contactService.submit(contact);
    },
  },

  /**
   * Event bus - subscribe to SDK events
   */
  on<T extends keyof SDKEventPayload>(event: T, handler: SDKEventHandler<T>): () => void {
    return eventBus.on(event, handler);
  },

  /**
   * Subscribe to event once
   */
  once<T extends keyof SDKEventPayload>(event: T, handler: SDKEventHandler<T>): () => void {
    return eventBus.once(event, handler);
  },

  /**
   * Unsubscribe from event
   */
  off<T extends keyof SDKEventPayload>(event: T, handler: SDKEventHandler<T>): void {
    return eventBus.off(event, handler);
  },

  /**
   * Destroy SDK instance and clean up resources
   */
  destroy(): void {
    eventBus.removeAllListeners();
    logger.info('SDK destroyed');
  },
};

// Default export
export default Puulso;

// Also expose on window for UMD builds
if (typeof window !== 'undefined') {
  (window as unknown as { Puulso: typeof Puulso }).Puulso = Puulso;
}
